import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, MessageSquare, User, Phone, Mail, Clock, Plus, Edit, CheckCircle, AlertTriangle, Trash2, UserPlus, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

type ReferralLeadStatus = 'neue_empfehlung' | 'terminiert' | 'kunde' | 'zurückgestellt' | 'kein_interesse' | 'offener_vorgang';

interface ReferralLead {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  message: string | null;
  status: ReferralLeadStatus;
  created_at: string;
  updated_at: string;
  referrer_customer_id: string | null;
  customers?: {
    full_name: string;
    created_at: string;
  } | null;
}

interface ReferralNote {
  id: string;
  content: string;
  created_at: string;
  created_by_user_id: string;
}

interface ReferralFollowUp {
  id: string;
  title: string;
  description: string | null;
  due_date: string;
  is_completed: boolean;
}

interface Customer {
  id: string;
  full_name: string;
  email: string;
  created_at: string;
}

interface ManualReferralForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  message: string;
  referrer_customer_id: string | null;
}

const statusLabels = {
  'neue_empfehlung': 'Neue Empfehlung',
  'terminiert': 'Terminiert',
  'kunde': 'Kunde',
  'zurückgestellt': 'Zurückgestellt',
  'kein_interesse': 'Kein Interesse',
  'offener_vorgang': 'Offener Vorgang'
};

const statusColors = {
  'neue_empfehlung': 'bg-blue-500',
  'terminiert': 'bg-green-500',
  'kunde': 'bg-emerald-500',
  'zurückgestellt': 'bg-yellow-500',
  'kein_interesse': 'bg-red-500',
  'offener_vorgang': 'bg-orange-500'
};

interface AllFollowUp {
  id: string;
  title: string;
  description: string | null;
  due_date: string;
  is_completed: boolean;
  referral_leads: {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string;
    status: ReferralLeadStatus;
  };
}

export default function ReferralCRM() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<ReferralLead[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('alle');
  const [showOnlySupporters, setShowOnlySupporters] = useState(false);
  const [selectedLead, setSelectedLead] = useState<ReferralLead | null>(null);
  const [notes, setNotes] = useState<ReferralNote[]>([]);
  const [followUps, setFollowUps] = useState<ReferralFollowUp[]>([]);
  const [allFollowUps, setAllFollowUps] = useState<AllFollowUp[]>([]);
  const [showFollowUpsView, setShowFollowUpsView] = useState(false);
  const [showCompletedFollowUps, setShowCompletedFollowUps] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newFollowUp, setNewFollowUp] = useState({
    title: '',
    description: '',
    due_date: undefined as Date | undefined
  });
  const [loading, setLoading] = useState(true);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualReferralForm, setManualReferralForm] = useState<ManualReferralForm>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    referrer_customer_id: null
  });

  useEffect(() => {
    fetchLeads();
    fetchCustomers();
    fetchAllFollowUps();
  }, [user]);

  const fetchLeads = async () => {
    if (!user) return;
    
    try {
      // Use secure function to get referral leads with proper data masking
      const { data, error } = await supabase.rpc('get_secure_referral_leads') as { 
        data: any[] | null, 
        error: any 
      };

      if (error) throw error;
      
      // Map the secure data to match existing interface
      const mappedLeads = (data || []).map((lead: any) => ({
        id: lead.id,
        first_name: lead.first_name,
        last_name: lead.last_name,
        email: lead.email,
        phone: lead.phone,
        message: lead.notes, // Map notes to message for backwards compatibility
        status: lead.status,
        created_at: lead.created_at,
        updated_at: lead.updated_at,
        referrer_customer_id: lead.referrer_customer_id,
        customers: lead.referrer_customer_id ? {
          full_name: lead.referrer_name || 'Unbekannter Empfehlungsgeber',
          created_at: lead.created_at
        } : null
      }));
      
      setLeads(mappedLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Fehler beim Laden der Empfehlungen');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('referral_leads')
        .update({ 
          status: newStatus as ReferralLeadStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === leadId ? { 
          ...lead, 
          status: newStatus as ReferralLeadStatus,
          updated_at: new Date().toISOString()
        } : lead
      ));
      
      toast.success('Status erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const deleteLead = async (leadId: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Empfehlung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      // First delete related notes and follow-ups
      const { error: notesError } = await supabase
        .from('referral_notes')
        .delete()
        .eq('referral_lead_id', leadId);

      if (notesError) throw notesError;

      const { error: followUpsError } = await supabase
        .from('referral_follow_ups')
        .delete()
        .eq('referral_lead_id', leadId);

      if (followUpsError) throw followUpsError;

      // Then delete the lead
      const { error } = await supabase
        .from('referral_leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;
      
      setLeads(leads.filter(lead => lead.id !== leadId));
      toast.success('Empfehlung erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Fehler beim Löschen der Empfehlung');
    }
  };

  const fetchCustomers = async () => {
    if (!user) return;
    
    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .single();

      if (!tenant) return;

      const { data, error } = await supabase
        .from('customers')
        .select('id, full_name, email, created_at')
        .eq('tenant_id', tenant.id)
        .order('full_name', { ascending: true });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const assignToExistingCustomer = async (leadId: string, customerId: string) => {
    try {
      const { error } = await supabase
        .from('referral_leads')
        .update({ 
          referrer_customer_id: customerId,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;
      
      // Update local state
      setLeads(leads.map(lead => 
        lead.id === leadId ? { 
          ...lead, 
          referrer_customer_id: customerId,
          updated_at: new Date().toISOString()
        } : lead
      ));
      
      toast.success('Empfehlung erfolgreich zugewiesen');
      fetchLeads(); // Refresh to get updated referrer data
    } catch (error) {
      console.error('Error assigning to customer:', error);
      toast.error('Fehler beim Zuweisen der Empfehlung');
    }
  };

  const createManualReferral = async () => {
    if (!user || !manualReferralForm.first_name || !manualReferralForm.email) {
      toast.error('Bitte füllen Sie mindestens Vorname und E-Mail aus');
      return;
    }

    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .single();

      if (!tenant) return;

      const { error } = await supabase
        .from('referral_leads')
        .insert({
          tenant_id: tenant.id,
          first_name: manualReferralForm.first_name,
          last_name: manualReferralForm.last_name || null,
          email: manualReferralForm.email,
          phone: manualReferralForm.phone || null,
          message: manualReferralForm.message || null,
          referrer_customer_id: manualReferralForm.referrer_customer_id || null,
          status: 'neue_empfehlung'
        });

      if (error) throw error;
      
      // Reset form
      setManualReferralForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: '',
        referrer_customer_id: null
      });
      
      setShowManualEntryModal(false);
      fetchLeads(); // Refresh leads
      toast.success('Empfehlung erfolgreich hinzugefügt');
    } catch (error) {
      console.error('Error creating manual referral:', error);
      toast.error('Fehler beim Erstellen der Empfehlung');
    }
  };

  const promoteToSupporter = async (lead: ReferralLead) => {
    if (!confirm(`Möchten Sie ${lead.first_name} ${lead.last_name} als Unterstützer einladen? Eine Registrierungs-E-Mail wird an ${lead.email} gesendet.`)) {
      return;
    }

    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user?.id)
        .single();

      if (!tenant) {
        toast.error('Tenant nicht gefunden');
        return;
      }

      const { data, error } = await supabase.functions.invoke('send-registration-invitation', {
        body: {
          leadId: lead.id,
          tenantId: tenant.id
        }
      });

      if (error) throw error;

      toast.success('Einladung erfolgreich gesendet!');
      
      // Refresh leads to show updated status
      fetchLeads();
      
    } catch (error) {
      console.error('Error promoting to supporter:', error);
      toast.error('Fehler beim Senden der Einladung');
    }
  };

  const fetchLeadDetails = async (lead: ReferralLead) => {
    setSelectedLead(lead);
    
    try {
      // Fetch notes
      const { data: notesData } = await supabase
        .from('referral_notes')
        .select('*')
        .eq('referral_lead_id', lead.id)
        .order('created_at', { ascending: false });

      // Fetch follow-ups
      const { data: followUpsData } = await supabase
        .from('referral_follow_ups')
        .select('*')
        .eq('referral_lead_id', lead.id)
        .order('due_date', { ascending: true });

      setNotes(notesData || []);
      setFollowUps(followUpsData || []);
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  const addNote = async () => {
    if (!selectedLead || !newNote.trim() || !user) return;

    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .single();

      if (!tenant) return;

      const { error } = await supabase
        .from('referral_notes')
        .insert({
          referral_lead_id: selectedLead.id,
          tenant_id: tenant.id,
          created_by_user_id: user.id,
          content: newNote
        });

      if (error) throw error;
      
      setNewNote('');
      fetchLeadDetails(selectedLead);
      toast.success('Notiz hinzugefügt');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Fehler beim Hinzufügen der Notiz');
    }
  };

  const addFollowUp = async () => {
    if (!selectedLead || !newFollowUp.title.trim() || !newFollowUp.due_date || !user) return;

    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .single();

      if (!tenant) return;

      const { error } = await supabase
        .from('referral_follow_ups')
        .insert({
          referral_lead_id: selectedLead.id,
          tenant_id: tenant.id,
          created_by_user_id: user.id,
          title: newFollowUp.title,
          description: newFollowUp.description,
          due_date: newFollowUp.due_date.toISOString()
        });

      if (error) throw error;
      
      setNewFollowUp({ title: '', description: '', due_date: undefined });
      fetchLeadDetails(selectedLead);
      toast.success('Wiedervorlage erstellt');
    } catch (error) {
      console.error('Error adding follow-up:', error);
      toast.error('Fehler beim Erstellen der Wiedervorlage');
    }
  };

  const markFollowUpCompleted = async (followUpId: string, title: string) => {
    if (!confirm(`Sind Sie sicher, dass Sie die Wiedervorlage "${title}" als erledigt markieren möchten?`)) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('referral_follow_ups')
        .update({ 
          is_completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', followUpId);

      if (error) throw error;
      
      if (selectedLead) {
        fetchLeadDetails(selectedLead);
      }
      // Refresh all follow-ups view
      fetchAllFollowUps();
      toast.success('Wiedervorlage als erledigt markiert');
    } catch (error) {
      console.error('Error marking follow-up as completed:', error);
      toast.error('Fehler beim Markieren der Wiedervorlage');
    }
  };

  const markFollowUpIncomplete = async (followUpId: string, title: string) => {
    if (!confirm(`Möchten Sie die Wiedervorlage "${title}" wieder als offen markieren?`)) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('referral_follow_ups')
        .update({ 
          is_completed: false,
          completed_at: null
        })
        .eq('id', followUpId);

      if (error) throw error;
      
      if (selectedLead) {
        fetchLeadDetails(selectedLead);
      }
      // Refresh all follow-ups view
      fetchAllFollowUps();
      toast.success('Wiedervorlage wieder als offen markiert');
    } catch (error) {
      console.error('Error marking follow-up as incomplete:', error);
      toast.error('Fehler beim Reaktivieren der Wiedervorlage');
    }
  };

  const isOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const isToday = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    return due.toDateString() === today.toDateString();
  };

  const fetchAllFollowUps = async () => {
    if (!user) return;
    
    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .single();

      if (!tenant) return;

      const { data, error } = await supabase
        .from('referral_follow_ups')
        .select(`
          id,
          title,
          description,
          due_date,
          is_completed,
          referral_leads!inner (
            id,
            first_name,
            last_name,
            email,
            status
          )
        `)
        .eq('tenant_id', tenant.id)
        .order('due_date', { ascending: true });

      if (error) throw error;
      setAllFollowUps((data as any[]) || []);
    } catch (error) {
      console.error('Error fetching all follow-ups:', error);
    }
  };

  const filteredLeads = showOnlySupporters 
    ? leads.filter(lead => lead.status === 'kunde')
    : (selectedStatus === 'alle' 
      ? leads 
      : leads.filter(lead => lead.status === selectedStatus));

  const openFollowUps = allFollowUps.filter(f => !f.is_completed);
  const completedFollowUps = allFollowUps.filter(f => f.is_completed);
  const displayedFollowUps = showCompletedFollowUps ? allFollowUps : openFollowUps;
  const overdueFollowUps = openFollowUps.filter(f => isOverdue(f.due_date));
  const todayFollowUps = openFollowUps.filter(f => isToday(f.due_date));

  if (loading) {
    return <div className="flex items-center justify-center h-64">Lädt...</div>;
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Empfehlungs-CRM</h1>
            <p className="text-muted-foreground">Verwalten Sie alle eingehenden Empfehlungen</p>
          </div>
          <Button onClick={() => setShowManualEntryModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Manuell hinzufügen
          </Button>
        </div>

      <div className="space-y-4">
        <div className="flex gap-4 items-center">
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Status</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant={showOnlySupporters ? 'default' : 'outline'}
            onClick={() => setShowOnlySupporters(!showOnlySupporters)}
            className="flex items-center gap-2"
          >
            <UserCheck className="h-4 w-4" />
            Nur Unterstützer
            <Badge variant="secondary" className="ml-1">
              {leads.filter(l => l.status === 'kunde').length}
            </Badge>
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={!showFollowUpsView && selectedStatus === 'alle' ? 'default' : 'outline'}
            onClick={() => {
              setShowFollowUpsView(false);
              setSelectedStatus('alle');
            }}
            className="flex items-center gap-2"
          >
            Alle Status ({leads.length})
          </Button>
          <Button
            variant={showFollowUpsView ? 'default' : 'outline'}
            onClick={() => setShowFollowUpsView(true)}
            className="flex items-center gap-2"
          >
            <Clock className="h-4 w-4" />
            Wiedervorlagen ({openFollowUps.length})
            {overdueFollowUps.length > 0 && (
              <Badge variant="destructive" className="ml-1">
                {overdueFollowUps.length}
              </Badge>
            )}
          </Button>
          {!showFollowUpsView && Object.entries(statusLabels).map(([status, label]) => {
            const count = leads.filter(lead => lead.status === status).length;
            return (
              <Button
                key={status}
                variant={selectedStatus === status ? 'default' : 'outline'}
                onClick={() => setSelectedStatus(status)}
                className="flex items-center gap-2"
              >
                <div className={cn("w-2 h-2 rounded-full", statusColors[status as keyof typeof statusColors])} />
                {label} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      {showFollowUpsView ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <h2 className="text-xl font-semibold">
                {showCompletedFollowUps ? 'Alle Wiedervorlagen' : 'Offene Wiedervorlagen'}
              </h2>
              {!showCompletedFollowUps && overdueFollowUps.length > 0 && (
                <Badge variant="destructive">
                  {overdueFollowUps.length} überfällig
                </Badge>
              )}
              {!showCompletedFollowUps && todayFollowUps.length > 0 && (
                <Badge variant="outline" className="border-orange-500 text-orange-600">
                  {todayFollowUps.length} heute fällig
                </Badge>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={!showCompletedFollowUps ? "default" : "outline"}
                size="sm"
                onClick={() => setShowCompletedFollowUps(false)}
              >
                Offene ({openFollowUps.length})
              </Button>
              <Button
                variant={showCompletedFollowUps ? "default" : "outline"}
                size="sm"
                onClick={() => setShowCompletedFollowUps(true)}
              >
                Alle ({allFollowUps.length})
              </Button>
            </div>
          </div>
          
          <div className="grid gap-3">
            {displayedFollowUps.map((followUp) => {
              const overdue = !followUp.is_completed && isOverdue(followUp.due_date);
              const today = !followUp.is_completed && isToday(followUp.due_date);
              
              return (
                <Card key={followUp.id} className={cn(
                  "transition-colors",
                  followUp.is_completed ? "bg-gray-50 border-gray-200" : "",
                  overdue ? "border-red-200 bg-red-50" : "",
                  today ? "border-orange-200 bg-orange-50" : ""
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className={cn("font-medium", followUp.is_completed && "line-through text-muted-foreground")}>
                            {followUp.title}
                          </h3>
                          {followUp.is_completed && (
                            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                              Erledigt
                            </Badge>
                          )}
                          {overdue && <AlertTriangle className="h-4 w-4 text-red-500" />}
                          {today && <Clock className="h-4 w-4 text-orange-500" />}
                        </div>
                        
                        {followUp.description && (
                          <p className={cn("text-sm text-muted-foreground mb-2", followUp.is_completed && "line-through")}>
                            {followUp.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {followUp.referral_leads.first_name} {followUp.referral_leads.last_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {followUp.referral_leads.email}
                          </span>
                          <Badge className={cn("text-white text-xs", statusColors[followUp.referral_leads.status])}>
                            {statusLabels[followUp.referral_leads.status]}
                          </Badge>
                        </div>
                        
                        <div className="mt-2">
                          <p className={cn(
                            "text-xs font-medium",
                            followUp.is_completed ? "text-muted-foreground line-through" :
                            overdue ? "text-red-600" : today ? "text-orange-600" : "text-muted-foreground"
                          )}>
                            Fällig: {format(new Date(followUp.due_date), 'dd.MM.yyyy')}
                            {overdue && " (Überfällig)"}
                            {today && " (Heute fällig)"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="shrink-0 ml-4 space-y-2">
                        {!followUp.is_completed ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markFollowUpCompleted(followUp.id, followUp.title)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Als erledigt markieren
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markFollowUpIncomplete(followUp.id, followUp.title)}
                            className="text-blue-600 hover:bg-blue-50"
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Wieder öffnen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {displayedFollowUps.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  {showCompletedFollowUps 
                    ? 'Keine Wiedervorlagen vorhanden' 
                    : 'Keine offenen Wiedervorlagen vorhanden'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLeads.map((lead) => (
          <Card key={lead.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">
                    {lead.first_name} {lead.last_name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4 mt-1">
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {lead.email}
                    </span>
                    {lead.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {lead.phone}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className={cn("text-white", statusColors[lead.status])}>
                      {statusLabels[lead.status]}
                    </Badge>
                    {lead.status === 'kunde' && (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        ✅ Unterstützer
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground text-right">
                    <div>Eingegangen: {format(new Date(lead.created_at), 'dd.MM.yyyy HH:mm')}</div>
                    <div>Bearbeitet: {format(new Date(lead.updated_at), 'dd.MM.yyyy HH:mm')}</div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lead.customers && (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      Empfohlen von: {lead.customers.full_name}
                    </p>
                    <p className="flex items-center gap-1 text-xs">
                      <Clock className="h-3 w-3" />
                      Kunde seit: {format(new Date(lead.customers.created_at), 'dd.MM.yyyy HH:mm')}
                    </p>
                  </div>
                )}
                {lead.message && (
                  <p className="text-sm bg-muted p-2 rounded">{lead.message}</p>
                )}
                <div className="flex gap-2 flex-wrap">
                  <Select
                    value={lead.status}
                    onValueChange={(newStatus) => updateLeadStatus(lead.id, newStatus)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    onClick={() => promoteToSupporter(lead)}
                    className="text-green-600 hover:bg-green-50"
                    disabled={lead.status === 'kunde'}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Als Unterstützer einladen
                  </Button>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="text-blue-600 hover:bg-blue-50">
                        <UserCheck className="h-4 w-4 mr-2" />
                        Zuweisen zu...
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-2">
                        <Label>Bestehenden Unterstützer auswählen:</Label>
                        <Select onValueChange={(customerId) => assignToExistingCustomer(lead.id, customerId)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Unterstützer wählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.full_name} - {customer.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" onClick={() => fetchLeadDetails(lead)}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                       <DialogHeader>
                         <DialogTitle>
                           {selectedLead?.first_name} {selectedLead?.last_name}
                         </DialogTitle>
                       </DialogHeader>
                      
                      <Tabs defaultValue="notes" className="mt-4">
                        <TabsList>
                          <TabsTrigger value="notes">Notizen</TabsTrigger>
                          <TabsTrigger value="followups">Wiedervorlagen</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="notes" className="space-y-4">
                          <div className="space-y-2">
                            <Label>Neue Notiz hinzufügen</Label>
                            <Textarea
                              placeholder="Notiz eingeben..."
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                            />
                            <Button onClick={addNote} size="sm">
                              <Plus className="h-4 w-4 mr-2" />
                              Notiz hinzufügen
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            {notes.map((note) => (
                              <div key={note.id} className="border p-3 rounded">
                                <p className="text-sm">{note.content}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {format(new Date(note.created_at), 'dd.MM.yyyy HH:mm')}
                                </p>
                              </div>
                            ))}
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="followups" className="space-y-4">
                          <div className="space-y-2">
                            <Label>Neue Wiedervorlage</Label>
                            <Input
                              placeholder="Titel..."
                              value={newFollowUp.title}
                              onChange={(e) => setNewFollowUp({ ...newFollowUp, title: e.target.value })}
                            />
                            <Textarea
                              placeholder="Beschreibung..."
                              value={newFollowUp.description}
                              onChange={(e) => setNewFollowUp({ ...newFollowUp, description: e.target.value })}
                            />
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className={cn("justify-start text-left font-normal", !newFollowUp.due_date && "text-muted-foreground")}>
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {newFollowUp.due_date ? format(newFollowUp.due_date, "dd.MM.yyyy") : "Datum wählen"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={newFollowUp.due_date}
                                  onSelect={(date) => setNewFollowUp({ ...newFollowUp, due_date: date })}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <Button onClick={addFollowUp} size="sm">
                              <Clock className="h-4 w-4 mr-2" />
                              Wiedervorlage erstellen
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            {followUps.map((followUp) => {
                              const overdue = isOverdue(followUp.due_date);
                              const today = isToday(followUp.due_date);
                              
                              return (
                                <div key={followUp.id} className={cn(
                                  "border p-3 rounded transition-colors",
                                  overdue && !followUp.is_completed ? "border-red-200 bg-red-50" : "",
                                  today && !followUp.is_completed ? "border-orange-200 bg-orange-50" : "",
                                  followUp.is_completed ? "opacity-60" : ""
                                )}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-medium">{followUp.title}</h4>
                                        {overdue && !followUp.is_completed && (
                                          <AlertTriangle className="h-4 w-4 text-red-500" />
                                        )}
                                        {today && !followUp.is_completed && (
                                          <Clock className="h-4 w-4 text-orange-500" />
                                        )}
                                      </div>
                                      {followUp.description && (
                                        <p className="text-sm text-muted-foreground mt-1">{followUp.description}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-2">
                                        <p className={cn(
                                          "text-xs",
                                          overdue && !followUp.is_completed ? "text-red-600 font-medium" : "text-muted-foreground"
                                        )}>
                                          Fällig: {format(new Date(followUp.due_date), 'dd.MM.yyyy')}
                                          {overdue && !followUp.is_completed && " (Überfällig)"}
                                          {today && !followUp.is_completed && " (Heute)"}
                                        </p>
                                        {followUp.is_completed && (
                                          <Badge variant="outline" className="text-green-600 border-green-600">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            Erledigt
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    
                                     {!followUp.is_completed && (
                                       <Button
                                         variant="ghost"
                                         size="sm"
                                         onClick={() => markFollowUpCompleted(followUp.id, followUp.title)}
                                         className="shrink-0 ml-2"
                                       >
                                         <CheckCircle className="h-4 w-4" />
                                       </Button>
                                     )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                         </TabsContent>
                       </Tabs>
                       
                       {/* Delete button moved to bottom right */}
                       <div className="flex justify-end mt-6 pt-4 border-t">
                         <Button 
                           variant="outline" 
                           size="sm"
                           onClick={() => selectedLead && deleteLead(selectedLead.id)}
                           className="text-red-600 hover:bg-red-50"
                         >
                           <Trash2 className="h-4 w-4 mr-2" />
                           Löschen
                         </Button>
                       </div>
                     </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>
          ))}
        </div>
      )}

      {!showFollowUpsView && filteredLeads.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">
              {selectedStatus === 'alle' 
                ? 'Noch keine Empfehlungen erhalten.' 
                : `Keine Empfehlungen mit Status "${statusLabels[selectedStatus as keyof typeof statusLabels]}".`
              }
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Manual Entry Modal */}
      <Dialog open={showManualEntryModal} onOpenChange={setShowManualEntryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Empfehlung manuell hinzufügen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Vorname *</Label>
                <Input
                  placeholder="Vorname"
                  value={manualReferralForm.first_name}
                  onChange={(e) => setManualReferralForm({ ...manualReferralForm, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input
                  placeholder="Nachname"
                  value={manualReferralForm.last_name}
                  onChange={(e) => setManualReferralForm({ ...manualReferralForm, last_name: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                placeholder="email@beispiel.de"
                value={manualReferralForm.email}
                onChange={(e) => setManualReferralForm({ ...manualReferralForm, email: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Telefon</Label>
              <Input
                placeholder="+49 123 456789"
                value={manualReferralForm.phone}
                onChange={(e) => setManualReferralForm({ ...manualReferralForm, phone: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Empfohlen durch</Label>
              <Select 
                value={manualReferralForm.referrer_customer_id || undefined}
                onValueChange={(value) => setManualReferralForm({ ...manualReferralForm, referrer_customer_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unterstützer wählen (optional)..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.full_name} - {customer.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nachricht/Notiz</Label>
              <Textarea
                placeholder="Optionale Nachricht oder Notiz..."
                value={manualReferralForm.message}
                onChange={(e) => setManualReferralForm({ ...manualReferralForm, message: e.target.value })}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowManualEntryModal(false)}>
                Abbrechen
              </Button>
              <Button onClick={createManualReferral}>
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
}